import { IPromise, IHttpService, IRequestShortcutConfig } from "angular";

import {
  IAutomationHistoryQueryParams,
  IAutomationHistory,
  IAutomation,
  IMagicField,
  IAutomationTargetPropertiesParams,
  IAutomationTask,
  INewAutomation,
  IPropertyNames,
  ISlackChannels,
  IHasSlackIntegration,
  ITargetPropertiesTypes,
  IAutomationTargetPropertiesWithAction,
  ICustomFieldProperties,
  PropertiesDictionary,
  AutomationTriggerType,
  AutomationTriggerPayload,
  ISupportedAutomationActionTypesRes,
  AutomationMagicFieldFormatType,
} from "../models/automation";
import { ICollection } from "@gtmhub/models";
import { EnvironmentService } from "@gtmhub/env";
import { localize } from "@gtmhub/localization";
import { IRestLayerRequest } from "@gtmhub/core";

export class AutomationService {
  public static $inject = ["$http", "EnvironmentService"];
  constructor(private $http: IHttpService, private env: EnvironmentService) {}

  private automationCallbackResult = null;
  private foundAutomationTask: IAutomationTask = null;
  private automationAncestorId = null;

  public createAutomation(newAutomation: INewAutomation): IPromise<IAutomation<AutomationTriggerType>> {
    const url = this.env.getApiEndpoint("/automation");

    return this.$http.post<{ automation: IAutomation<AutomationTriggerType> }>(url, newAutomation).then((response) => response.data.automation);
  }

  public getPropertiesDictionary(): PropertiesDictionary {
    return {
      ChildrenCount: "Children Objectives",
      Attainment: "Progress",
      SessionID: "Session",
      OwnerID: "Assignee",
      OwnerIds: "Owners",
      DateCreated: "Creation date",
      DateFrom: "Starting date",
      DateTo: "Ending date",
      CreatedByID: "The user who created",
      ModifiedByID: "The user who modified",
      AggregatedAttainment: "Aggregated progress",
      MetricsCount: "Number of Key Results",
      Parent: "Parent OKR",
      TasksCount: "Number of tasks",
      GoalID: "Objective",
      SoftDueDate: "Soft due date",
      InitialValue: "Initial value",
      DueDate: "Due date",
      LatestSnapshotAt: "Last updated",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Goal_SessionID: "Session",
      IsActive: "Active",
    };
  }

  public getRuleDictionary(): any {
    const alphabetic = {
      longer: localize("is_longer_than"),
      shorter: localize("is_shorter_than"),
      contains: localize("contains"),
      doesNotContain: localize("does_not_contain"),
      notEqual: localize("is_not_equal_to"),
      equals: localize("equals_to"),
    };

    const constainsNotContains = {
      contains: localize("contains"),
      doesNotContain: localize("does_not_contain"),
    };

    return {
      alphabetic: alphabetic,
      id: {
        notEqual: localize("is_not"),
        equals: localize("is"),
        contains: localize("contains"),
        doesNotContain: localize("does_not_contain"),
      },
      date: {
        notEqual: localize("is_not"),
        equals: localize("is"),
        greater: localize("is_after"),
        less: localize("is_before"),
        notInLast: localize("not_in_the_last"),
        inLast: localize("in_the_last"),
      },
      numeric: {
        notEqual: localize("is_not_equal_to"),
        equals: localize("equals"),
        greater: localize("is_greater_than"),
        less: localize("is_lower_than"),
        compareTo: localize("compare_to_previous_value"),
        previousValue: localize("previous_value"),
        differenceEquals: localize("equals"),
        differenceNotEqual: localize("is_not_equal_to"),
        differenceLess: localize("is_lower_than"),
        differenceGreater: localize("is_greater_than"),
        difference: localize("difference"),
      },
      boolean: {
        equals: localize("is"),
        notEqual: localize("is_not"),
      },
      dropdown: alphabetic,
      multiselect: constainsNotContains,
      selectassignee: constainsNotContains,
    };
  }

  public getAutomationActionTypes<T extends AutomationTriggerType>(objectType: T): IPromise<ISupportedAutomationActionTypesRes<T>> {
    const url = this.env.getApiEndpoint(`/automation/actiontypes/${objectType}`);

    return this.$http.get<ISupportedAutomationActionTypesRes<T>>(url).then((response) => response.data);
  }

  public getActionTypesWithSensitiveData(): IPromise<{ sensitiveDataExposingActionTypes: string[]; count: number }> {
    const url = this.env.getApiEndpoint("/automation/actiontypes/sensitive-data");

    return this.$http.get<{ sensitiveDataExposingActionTypes: string[]; count: number }>(url).then((response) => response.data);
  }

  public getDisplayRuleDictionary(): any {
    const defaultValues = {
      longer: localize("is_longer_than"),
      shorter: localize("is_shorter_than"),
      contains: localize("contains"),
      doesNotContain: localize("does_not_contain"),
      notEqual: localize("is_not_equal_to"),
      equals: localize("equals_to"),
      greater: localize("is_greater_than"),
      less: localize("is_lower_than"),
      compareTo: localize("compare_to_previous_value"),
      differenceEquals: localize("equals"),
      differenceNotEqual: localize("is_not_equal_to"),
      differenceLess: localize("is_lower_than"),
      differenceGreater: localize("is_greater_than"),
      difference: localize("difference"),
    };

    return {
      default: defaultValues,
      date: {
        ...defaultValues,
        greater: localize("is_after"),
        less: localize("is_before"),
      },
      dateLast: {
        less: localize("not_in_the_last"),
        greater: localize("in_the_last"),
      },
      session: {
        notEqual: localize("is_not"),
        equals: localize("is"),
      },
      goalSessionId: {
        notEqual: localize("is_not"),
        equals: localize("is"),
      },
      goal: {
        notEqual: localize("is_not"),
        equals: localize("is"),
        contains: localize("contains"),
        doesNotContain: localize("does_not_contain"),
      },
      user: {
        notEqual: localize("is_not"),
        equals: localize("is"),
      },
    };
  }

  public getConditionPropertyNames(): IPropertyNames {
    return {
      session: "SessionID",
      name: "Name",
      description: "Description",
      ownerId: "OwnerID",
      ownerIds: "OwnerIds",
      dateCreated: "DateCreated",
      dateFrom: "DateFrom",
      dateTo: "DateTo",
      createdById: "CreatedByID",
      modifiedById: "ModifiedByID",
      attainment: "Attainment",
      aggregatedAttainment: "AggregatedAttainment",
      childrenCount: "ChildrenCount",
      metricsCount: "MetricsCount",
      private: "Private",
      parent: "Parent",
      tasksCount: "TasksCount",
      objective: "GoalID",
      goalSessionId: "Goal_SessionID",
    };
  }

  public getCustomFieldProperties(): ICustomFieldProperties {
    return {
      dropdown: "dropdown",
      boolean: "boolean",
      multiselect: "multiselect",
      selectAssignee: "selectassignee",
    };
  }

  public deleteAutomation(automation: IAutomation<AutomationTriggerType>): IPromise<unknown> {
    const url = this.env.getApiEndpoint(`/automation/${automation.id}`);

    return this.$http.delete<unknown>(url).then((response) => response.data);
  }

  public deleteAutomationCondition(automation: IAutomation<AutomationTriggerType>): IPromise<IAutomation<AutomationTriggerType>> {
    const url = this.env.getApiEndpoint(`/automation/${automation.id}`);

    return this.$http.put<IAutomation<AutomationTriggerType>>(url, automation).then((response) => response.data);
  }

  public getAllAutomations(params: IRestLayerRequest): IPromise<ICollection<IAutomation<AutomationTriggerType>>> {
    const url = this.env.getApiEndpointV2("/automation");

    return this.$http.get<ICollection<IAutomation<AutomationTriggerType>>>(url, { params }).then((response) => response.data);
  }

  public getSlackChannels(): IPromise<ISlackChannels> {
    const url = this.env.getApiEndpoint(`/automation/slack/channels`);

    return this.$http.get<ISlackChannels>(url).then((response) => response.data);
  }

  public hasSlackIntegration(): IPromise<IHasSlackIntegration> {
    const url = this.env.getApiEndpoint(`/automation/slack/has-integration`);

    return this.$http.get<IHasSlackIntegration>(url).then((response) => response.data);
  }

  public updateAutomation(automation: IAutomation<AutomationTriggerType>): IPromise<IAutomation<AutomationTriggerType>> {
    const url = this.env.getApiEndpoint(`/automation/${automation.id}`);

    return this.$http.patch<IAutomation<AutomationTriggerType>>(url, automation).then((response) => response.data);
  }

  public executeAutomation(automation: IAutomation<AutomationTriggerType>): IPromise<IAutomation<AutomationTriggerType>> {
    const url = this.env.getApiEndpoint(`/automation/${automation.id}`);

    return this.$http.post<IAutomation<AutomationTriggerType>>(url, automation).then((response) => response.data);
  }

  public getAutomation(id: string): IPromise<IAutomation<AutomationTriggerType>> {
    const url = this.env.getApiEndpoint(`/automation/${id}`);

    return this.$http.get<IAutomation<AutomationTriggerType>>(url).then((response) => response.data);
  }

  public getAutomationHistory(automationId: string, queryParams: IAutomationHistoryQueryParams): IPromise<ICollection<IAutomationHistory>> {
    const url = this.env.getApiEndpoint(`/automation/${automationId}/history`);
    const query: IRequestShortcutConfig = {
      params: queryParams,
    };

    return this.$http.get<ICollection<IAutomationHistory>>(url, query).then((response) => response.data);
  }

  public getAutomationTask(
    automation: IAutomation<AutomationTriggerType>,
    automationTaskId: string,
    callback?: (automationTask: IAutomationTask, automationAncestorId?: string) => unknown
  ): IAutomationTask | any {
    for (const key in automation) {
      // eslint-disable-next-line no-prototype-builtins
      if (automation.hasOwnProperty(key) && (key === "automationTask" || key === "nextAutomationTask")) {
        if (automation[key] && automation[key].id === automationTaskId) {
          this.foundAutomationTask = automation[key];
          this.automationCallbackResult = typeof callback === "function" ? callback(this.foundAutomationTask, this.automationAncestorId) : null;
          break;
        }

        this.automationAncestorId = automation[key].id;
        this.getAutomationTask(automation[key], automationTaskId, callback);
      }
    }

    return this.automationCallbackResult || this.foundAutomationTask;
  }

  public getTargetPropertiesTypes(queryParams: IAutomationTargetPropertiesParams): IPromise<ITargetPropertiesTypes> {
    const url = this.env.getApiEndpoint(`/automation/target/properties`);
    const query: IRequestShortcutConfig = {
      params: queryParams,
    };

    return this.$http.get<ITargetPropertiesTypes>(url, query).then((response) => response.data);
  }

  public getTargetWithActions(): IPromise<IAutomationTargetPropertiesWithAction<AutomationTriggerType>> {
    const url = this.env.getApiEndpoint(`/automation/target`);

    return this.$http.get<IAutomationTargetPropertiesWithAction<AutomationTriggerType>>(url).then((response) => response.data);
  }

  public createTriggerEvent(automationId: string, automationTask: AutomationTriggerPayload): IPromise<unknown> {
    const url = this.env.getApiEndpoint(`/automation/${automationId}`);

    return this.$http.patch(url, automationTask).then((response) => response.data);
  }

  public cloneAutomation(id: string): IPromise<IAutomation<AutomationTriggerType>> {
    const url = this.env.getApiEndpoint(`/automation/${id}/clone`);

    return this.$http
      .post<{ automation: IAutomation<AutomationTriggerType> }>(url, null, {
        params: { gtmhubAdditionalParams: { cloned: true } },
      })
      .then((response) => response.data.automation);
  }

  public getMagicFields(
    options: { formatType?: AutomationMagicFieldFormatType; targetType?: AutomationTriggerType } = { formatType: "email", targetType: "goal" }
  ): IPromise<IMagicField[]> {
    const url = this.env.getApiEndpoint(`/automation/magicfields/${options.targetType}`);
    const query: IRequestShortcutConfig = {
      params: {
        formatType: options.formatType,
      },
    };
    return this.$http.get<IMagicField[]>(url, query).then((response) => response.data);
  }

  public getSlackButton(): IPromise<string> {
    const url = this.env.getApiEndpoint(`/automation/slack-button`);

    return this.$http.get<string>(url).then((response) => decodeURIComponent(response.data));
  }
}
